import React, { useState, useRef, useEffect } from 'react';
import { productsAPI, matchesAPI } from '../utils/api';
import Modal from '../components/Modal';
import ImageCarousel from '../components/ImageCarousel';
import SuccessIndicator from '../components/SuccessIndicator';
import '../styles/ProductMatchingPage.css';

const ProductMatchingPage = () => {
  // Helper function to check if price difference is more than 30%
  const isPriceDifferenceSignificant = (sourcePrice, targetPrice) => {
    if (!sourcePrice || !targetPrice) return false;
    
    const priceDifference = Math.abs(sourcePrice - targetPrice);
    const percentageDifference = (priceDifference / sourcePrice) * 100;
    
    return percentageDifference > 30;
  };
  
  // Helper function to extract dimensions from technical_data
  const extractDimensions = (technicalData) => {
    if (!technicalData) return null;
    
    try {
      const data = typeof technicalData === 'string' ? JSON.parse(technicalData) : technicalData;
      
      // Check if height, width, and depth exist
      if (data.height !== undefined && data.width !== undefined && data.depth !== undefined) {
        return {
          height: data.height,
          width: data.width,
          depth: data.depth
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing technical data:', error);
      return null;
    }
  };
  
  // Helper function to strip HTML tags from text
  const stripHtmlTags = (html) => {
    if (!html) return '';
    
    // Create a temporary div element
    const tempDiv = document.createElement('div');
    // Set the HTML content
    tempDiv.innerHTML = html;
    // Return the text content only (without HTML tags)
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  
  const stringToJson = (jsonData, path = null) => {
    if (!jsonData) return null;
    
    try {
      // Parse the JSON if it's a string
      const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      
      // If no path is provided, return the entire object
      if (!path) return data;
      
      // Handle nested property access
      const properties = path.split('.');
      let result = data;
      
      // Navigate through the nested properties
      for (const prop of properties) {
        if (result && typeof result === 'object' && prop in result) {
          result = result[prop];
        } else {
          // Property doesn't exist
          console.log(`Property ${prop} not found in path ${path}`);
          return null;
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return null;
    }
  };
  
  // Helper function to extract measurements from technical data
  const extractMeasurements = (technicalData) => {
    if (!technicalData) return null;
    
    try {
      // Parse the JSON if it's a string
      const data = typeof technicalData === 'string' ? JSON.parse(technicalData) : technicalData;
      const rawData = typeof technicalData === 'string' ? technicalData : JSON.stringify(data);
      
      // Initialize variables to store measurements
      let breite = null;
      let länge = null;
      let höhe = null;
      let materialstärke = null;
      let durchmesser = null;
      
      // First check Maßangaben section (priority)
      const massangaben = data.Maßangaben;
      if (massangaben) {
        // Case 1: Maßangaben is an array of strings
        if (Array.isArray(massangaben)) {
          massangaben.forEach(item => {
            if (typeof item === 'string') {
              if (item.startsWith('Breite:')) {
                breite = item.replace('Breite:', '').trim();
              } else if (item.startsWith('Länge:')) {
                länge = item.replace('Länge:', '').trim();
              } else if (item.startsWith('Höhe:')) {
                höhe = item.replace('Höhe:', '').trim();
              } else if (item.startsWith('Materialstärke:')) {
                materialstärke = item.replace('Materialstärke:', '').trim();
              } else if (item.startsWith('Durchmesser:')) {
                durchmesser = item.replace('Durchmesser:', '').trim();
              }
            }
          });
        } 
        // Case 2: Maßangaben is an object with properties
        else if (typeof massangaben === 'object' && !Array.isArray(massangaben)) {
          // Convert the object to a string to search for measurements
          const massangabenStr = JSON.stringify(massangaben);
          
          // Extract measurements using more precise regex patterns
          // These patterns look for the measurement label followed by a value with units
          // and stop at the next label or end of string
          const breiteMatch = massangabenStr.match(/Breite:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
          const längeMatch = massangabenStr.match(/Länge:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
          const höheMatch = massangabenStr.match(/Höhe:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
          const materialstärkeMatch = massangabenStr.match(/Materialstärke:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
          const durchmesserMatch = massangabenStr.match(/Durchmesser:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
          
          if (breiteMatch && breiteMatch[1]) breite = breiteMatch[1].trim();
          if (längeMatch && längeMatch[1]) länge = längeMatch[1].trim();
          if (höheMatch && höheMatch[1]) höhe = höheMatch[1].trim();
          if (materialstärkeMatch && materialstärkeMatch[1]) materialstärke = materialstärkeMatch[1].trim();
          if (durchmesserMatch && durchmesserMatch[1]) durchmesser = durchmesserMatch[1].trim();
          
          // Also check individual properties directly
          Object.entries(massangaben).forEach(([key, value]) => {
            if (typeof value === 'string') {
              // For direct property access, extract just the measurement part
              if (key === 'Breite' && !breite) {
                // Extract just the measurement part (e.g., "31 cm" from "31 cm Breite: 31 cm...")
                const match = value.match(/^([0-9,.]+\s*[a-zA-Z]+)/);
                breite = match ? match[1].trim() : value.trim();
              } else if (key === 'Länge' && !länge) {
                const match = value.match(/^([0-9,.]+\s*[a-zA-Z]+)/);
                länge = match ? match[1].trim() : value.trim();
              } else if (key === 'Höhe' && !höhe) {
                const match = value.match(/^([0-9,.]+\s*[a-zA-Z]+)/);
                höhe = match ? match[1].trim() : value.trim();
              } else if (key === 'Materialstärke' && !materialstärke) {
                const match = value.match(/^([0-9,.]+\s*[a-zA-Z]+)/);
                materialstärke = match ? match[1].trim() : value.trim();
              } else if (key === 'Durchmesser' && !durchmesser) {
                const match = value.match(/^([0-9,.]+\s*[a-zA-Z]+)/);
                durchmesser = match ? match[1].trim() : value.trim();
              }
            }
          });
        }
      }
      
      // Then check Eigenschaften section for any missing measurements
      const eigenschaften = data.Eigenschaften;
      if (eigenschaften) {
        // Case 1: Eigenschaften is an array of strings
        if (Array.isArray(eigenschaften)) {
          eigenschaften.forEach(item => {
            if (typeof item === 'string') {
              // Only replace values that weren't found in Maßangaben
              if (!breite && item.startsWith('Breite:')) {
                breite = item.replace('Breite:', '').trim();
              } else if (!länge && item.startsWith('Länge:')) {
                länge = item.replace('Länge:', '').trim();
              } else if (!höhe && item.startsWith('Höhe:')) {
                höhe = item.replace('Höhe:', '').trim();
              } else if (!materialstärke && item.startsWith('Materialstärke:')) {
                materialstärke = item.replace('Materialstärke:', '').trim();
              } else if (!durchmesser && item.startsWith('Durchmesser:')) {
                durchmesser = item.replace('Durchmesser:', '').trim();
              }
            }
          });
        }
        // Case 2: Eigenschaften is an object with properties
        else if (typeof eigenschaften === 'object' && !Array.isArray(eigenschaften)) {
          // Convert the object to a string to search for measurements
          const eigenschaftenStr = JSON.stringify(eigenschaften);
          
          // Only extract measurements that weren't found in Maßangaben
          // Using the same improved regex patterns
          if (!breite) {
            const breiteMatch = eigenschaftenStr.match(/Breite:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
            if (breiteMatch && breiteMatch[1]) breite = breiteMatch[1].trim();
          }
          if (!länge) {
            const längeMatch = eigenschaftenStr.match(/Länge:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
            if (längeMatch && längeMatch[1]) länge = längeMatch[1].trim();
          }
          if (!höhe) {
            const höheMatch = eigenschaftenStr.match(/Höhe:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
            if (höheMatch && höheMatch[1]) höhe = höheMatch[1].trim();
          }
          if (!materialstärke) {
            const materialstärkeMatch = eigenschaftenStr.match(/Materialstärke:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
            if (materialstärkeMatch && materialstärkeMatch[1]) materialstärke = materialstärkeMatch[1].trim();
          }
          if (!durchmesser) {
            const durchmesserMatch = eigenschaftenStr.match(/Durchmesser:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
            if (durchmesserMatch && durchmesserMatch[1]) durchmesser = durchmesserMatch[1].trim();
          }
          
          // Also check individual properties directly
          Object.entries(eigenschaften).forEach(([key, value]) => {
            if (typeof value === 'string') {
              if (key === 'Breite' && !breite) {
                // Extract just the measurement part
                const match = value.match(/^([0-9,.]+\s*[a-zA-Z]+)/);
                breite = match ? match[1].trim() : value.trim();
              } else if (key === 'Länge' && !länge) {
                const match = value.match(/^([0-9,.]+\s*[a-zA-Z]+)/);
                länge = match ? match[1].trim() : value.trim();
              } else if (key === 'Höhe' && !höhe) {
                const match = value.match(/^([0-9,.]+\s*[a-zA-Z]+)/);
                höhe = match ? match[1].trim() : value.trim();
              } else if (key === 'Materialstärke' && !materialstärke) {
                const match = value.match(/^([0-9,.]+\s*[a-zA-Z]+)/);
                materialstärke = match ? match[1].trim() : value.trim();
              } else if (key === 'Durchmesser' && !durchmesser) {
                const match = value.match(/^([0-9,.]+\s*[a-zA-Z]+)/);
                durchmesser = match ? match[1].trim() : value.trim();
              }
            }
          });
        }
      }
      
      // Final fallback: Use regex on the entire technical_data string for any measurements still missing
      // Using the improved regex patterns
      if (!breite) {
        const breiteMatch = rawData.match(/Breite:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
        if (breiteMatch && breiteMatch[1]) breite = breiteMatch[1].trim();
      }
      
      if (!länge) {
        const längeMatch = rawData.match(/Länge:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
        if (längeMatch && längeMatch[1]) länge = längeMatch[1].trim();
      }
      
      if (!höhe) {
        const höheMatch = rawData.match(/Höhe:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
        if (höheMatch && höheMatch[1]) höhe = höheMatch[1].trim();
      }
      
      if (!materialstärke) {
        const materialstärkeMatch = rawData.match(/Materialstärke:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
        if (materialstärkeMatch && materialstärkeMatch[1]) materialstärke = materialstärkeMatch[1].trim();
      }
      
      if (!durchmesser) {
        const durchmesserMatch = rawData.match(/Durchmesser:\s*([0-9,.]+\s*[a-zA-Z]+)(?=\s+[A-Z]|$)/);
        if (durchmesserMatch && durchmesserMatch[1]) durchmesser = durchmesserMatch[1].trim();
      }
      
      console.log('Extracted measurements:', { breite, länge, höhe, materialstärke, durchmesser });
      
      // Format measurements based on available values
      const measurements = [];
      
      // Add dimensions if available
      if (höhe) measurements.push(`H: ${höhe}`);
      if (breite) measurements.push(`B: ${breite}`);
      if (länge) measurements.push(`L: ${länge}`);
      
      // Add material thickness if available
      if (materialstärke) measurements.push(`Stärke: ${materialstärke}`);
      
      // Add diameter if available
      if (durchmesser) measurements.push(`Ø: ${durchmesser}`);
      
      // Join all measurements with a separator
      return measurements.length > 0 ? measurements.join(' × ') : null;
    } catch (error) {
      console.error('Error extracting measurements:', error);
      return null;
    }
  };
  
  // Helper function to render JSON as nested unordered lists
  const renderJsonAsLists = (jsonData) => {
    if (!jsonData) return null;
    
    try {
      // Parse the JSON if it's a string
      const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      
      // Function to recursively render objects and arrays
      const renderValue = (value) => {
        if (value === null || value === undefined) {
          return <span className="json-null">null</span>;
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          return renderObject(value);
        } else if (Array.isArray(value)) {
          return renderArray(value);
        } else if (typeof value === 'boolean') {
          return <span className="json-boolean">{value.toString()}</span>;
        } else if (typeof value === 'number') {
          return <span className="json-number">{value}</span>;
        } else {
          return <span className="json-string">{value.toString()}</span>;
        }
      };
      
      // Render an object as a list
      const renderObject = (obj) => {
        const entries = Object.entries(obj);
        if (entries.length === 0) return <span className="json-empty">{"{}"}</span>;
        
        return (
          <ul className="json-object">
            {entries.map(([key, value], index) => (
              <li key={index} className="json-property">
                <span className="json-key">{key}:</span> {renderValue(value)}
              </li>
            ))}
          </ul>
        );
      };
      
      // Render an array as a list
      const renderArray = (arr) => {
        if (arr.length === 0) return <span className="json-empty">[]</span>;
        
        return (
          <ul className="json-array">
            {arr.map((item, index) => (
              <li key={index} className="json-item">
                {renderValue(item)}
              </li>
            ))}
          </ul>
        );
      };
      
      // Start rendering from the top level
      return renderValue(data);
    } catch (error) {
      console.error('Error parsing or rendering JSON:', error);
      return <span className="json-error">Error parsing technical data</span>;
    }
  };
  const [sourceProduct, setSourceProduct] = useState(null);
  const [currentObiProduct, setCurrentObiProduct] = useState(null);
  const [eanInput, setEanInput] = useState('');
  const [articleNumberInput, setArticleNumberInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [missingEan, setMissingEan] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [proposedMatches, setProposedMatches] = useState([]);
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false);
  const [showSuccessIndicator, setShowSuccessIndicator] = useState(false);
  const eanInputRef = useRef(null);
  const [prevEAN, setPrevEAN] = useState(0);
  const articleNumberInputRef = useRef(null);
  
  // Audio elements for beep sounds
  const positiveBeepRef = useRef(null);
  const negativeBeepRef = useRef(null);
  
  // Functions to play beep sounds
  const playPositiveBeep = () => {
    try {
      if (positiveBeepRef.current) {
        positiveBeepRef.current.currentTime = 0;
        positiveBeepRef.current.play().catch(e => console.error("Error playing positive beep:", e));
      }
    } catch (e) {
      console.error("Error playing positive beep:", e);
    }
  };

  const playNegativeBeep = () => {
    try {
      if (negativeBeepRef.current) {
        negativeBeepRef.current.currentTime = 0;
        negativeBeepRef.current.play().catch(e => console.error("Error playing negative beep:", e));
      }
    } catch (e) {
      console.error("Error playing negative beep:", e);
    }
  };

  // Reset state when a new search is performed
  const resetState = () => {
    setSourceProduct(null);
    setCurrentObiProduct(null);
    setCurrentMatchIndex(0);
    setProposedMatches([]);
  };

  // Effect to focus the input field when component mounts
  useEffect(() => {
    if (eanInputRef.current) {
      eanInputRef.current.focus();
      
      // Add a small delay to ensure the focus is applied
      setTimeout(() => {
        if (document.activeElement !== eanInputRef.current) {
          eanInputRef.current.focus();
        }
      }, 100);
    }
  }, []);
  
  // Effect to focus the input field after loading state changes
  useEffect(() => {
    if (!isLoading && eanInputRef.current) {
      // Use setTimeout to ensure focus happens after React rendering
      setTimeout(() => {
        eanInputRef.current.focus();
        
        // Add a small delay to ensure the focus is applied
        setTimeout(() => {
          if (document.activeElement !== eanInputRef.current) {
            eanInputRef.current.focus();
          }
        }, 100);
      }, 0);
    }
  }, [isLoading]);
  
  // No longer needed with the new approach
  // const handleInputBlur = () => {
  //   setInputReadOnly(true);
  // };

  // Effect to handle showing the next proposed match
  useEffect(() => {
    if (proposedMatches.length > 0 && currentMatchIndex < proposedMatches.length) {
      setCurrentObiProduct(proposedMatches[currentMatchIndex].obiProduct);
    } else if (proposedMatches.length > 0 && currentMatchIndex >= proposedMatches.length) {
      // All matches have been rejected, show confirmation dialog
      setShowRejectConfirmation(true);
    }
  }, [currentMatchIndex, proposedMatches]);

  // Handle search
  const handleSearch = async () => {
    if (!eanInput.trim()) return;
    
    setIsLoading(true);
    resetState();
    
    try {
      // Get product by EAN      
      const product = await productsAPI.getProductByEAN(eanInput);
      
      // Check for WGR exclusion
      if (product.wgr && product.wgr.ausschluss) {
        setModalMessage('Ausschluss WGR');
        setIsModalOpen(true);
        resetState();
        // Play negative beep for WGR exclusion
        playNegativeBeep();
        return;
      }
      
      // Check for no matches
      if (!product.matches || product.matches.length === 0) {
        setModalMessage('Keine Matches vorhanden');
        setIsModalOpen(true);
        resetState();
        // Play negative beep for no matches
        playNegativeBeep();
        return;
      }
      
      // Transform the product to maintain compatibility with frontend
      const transformedProduct = {
        ...product,
        status: product.matching_status, // Map matching_status to status for backward compatibility
        image: product.image_url ? (Array.isArray(product.image_url) ? product.image_url : [product.image_url]) : [],
        description: product.description || '', // Ensure description is at least an empty string
        detailed_description: product.detailed_description || '', // Ensure detailed_description is at least an empty string
        dimensions: product.dimensions || '', // Ensure dimensions is at least an empty string
        price: product.price || 0, // Ensure price has a default value
        ean: product.ean || '' // Ensure EAN is available
      };
      
      setSourceProduct(transformedProduct);
      
      // Play appropriate sound based on product status
      if (transformedProduct.status === 'proposed') {
        // Play positive beep for products with proposed matches
        playPositiveBeep();
      } else {
        // Play negative beep for already matched products
        playNegativeBeep();
      }
      
      // Handle different product statuses
      switch (transformedProduct.status) {
        case 'proposed':
          // Extract proposed matches
          const proposed = product.matches.filter(match => match.status === 'proposed');
          
          // Transform matches to include obiProduct property for compatibility
          const transformedMatches = proposed.map(match => {
            // Ensure obi_product has default values for required fields
            const obiProduct = match.obi_product || {};
            
            // Parse image_url if it's a JSON string
            let imageArray = [];
            if (obiProduct.image_url) {
              if (typeof obiProduct.image_url === 'string' && obiProduct.image_url.startsWith('[') && obiProduct.image_url.endsWith(']')) {
                try {
                  imageArray = JSON.parse(obiProduct.image_url);
                } catch (e) {
                  console.error('Error parsing OBI product image URL JSON:', e);
                  imageArray = [obiProduct.image_url];
                }
              } else if (Array.isArray(obiProduct.image_url)) {
                imageArray = obiProduct.image_url;
              } else {
                imageArray = [obiProduct.image_url];
              }
            }
            
                return {
                  ...match,
                  obiProduct: {
                    ...obiProduct,
                    name: obiProduct.name || '',
                    dimensions: obiProduct.dimensions || '',
                    price: obiProduct.price || 0,
                    description: obiProduct.description || '',
                    ean: obiProduct.ean || '',
                    image: imageArray,
                    wgr: obiProduct.wgr || {}, // Include the WGR data
                    technical_data: obiProduct.technical_data || '' // Include technical_data
                  }
                };
          });
          
          // Sort matches by position in ascending order
          const sortedMatches = transformedMatches.sort((a, b) => {
            // Handle undefined positions by treating them as highest value
            const posA = a.position !== undefined ? a.position : Infinity;
            const posB = b.position !== undefined ? b.position : Infinity;
            return posA - posB;
          });
          
          setProposedMatches(sortedMatches);
          setCurrentMatchIndex(0);
          break;
          
        case 'match_ean':
          // Find the matched product with EAN status
          const eanMatch = product.matches.find(match => match.status === 'match_ean');
          if (eanMatch) {
            // Ensure obi_product has default values for required fields
            const obiProduct = eanMatch.obi_product || {};
            
            // Parse image_url if it's a JSON string
            let imageArray = [];
            if (obiProduct.image_url) {
              if (typeof obiProduct.image_url === 'string' && obiProduct.image_url.startsWith('[') && obiProduct.image_url.endsWith(']')) {
                try {
                  imageArray = JSON.parse(obiProduct.image_url);
                } catch (e) {
                  console.error('Error parsing OBI product image URL JSON:', e);
                  imageArray = [obiProduct.image_url];
                }
              } else if (Array.isArray(obiProduct.image_url)) {
                imageArray = obiProduct.image_url;
              } else {
                imageArray = [obiProduct.image_url];
              }
            }
            
            setCurrentObiProduct({
              ...obiProduct,
              name: obiProduct.name || '',
              dimensions: obiProduct.dimensions || '',
              price: obiProduct.price || 0,
              description: obiProduct.description || '',
              ean: obiProduct.ean || '',
              image: imageArray,
              wgr: obiProduct.wgr || {}, // Include the WGR data
              technical_data: obiProduct.technical_data || '' // Include technical_data
            });
          }
          break;
          
        case 'match_kombinierbar':
        case 'match_nicht_kombinierbar':
          // Find the matched product
          const matchedProduct = product.matches.find(
            match => match.status === 'match_kombinierbar' || match.status === 'match_nicht_kombinierbar'
          );
          if (matchedProduct) {
            // Ensure obi_product has default values for required fields
            const obiProduct = matchedProduct.obi_product || {};
            
            // Parse image_url if it's a JSON string
            let imageArray = [];
            if (obiProduct.image_url) {
              if (typeof obiProduct.image_url === 'string' && obiProduct.image_url.startsWith('[') && obiProduct.image_url.endsWith(']')) {
                try {
                  imageArray = JSON.parse(obiProduct.image_url);
                } catch (e) {
                  console.error('Error parsing OBI product image URL JSON:', e);
                  imageArray = [obiProduct.image_url];
                }
              } else if (Array.isArray(obiProduct.image_url)) {
                imageArray = obiProduct.image_url;
              } else {
                imageArray = [obiProduct.image_url];
              }
            }
            
            setCurrentObiProduct({
              ...obiProduct,
              name: obiProduct.name || '',
              dimensions: obiProduct.dimensions || '',
              price: obiProduct.price || 0,
              description: obiProduct.description || '',
              ean: obiProduct.ean || '',
              image: imageArray,
              wgr: obiProduct.wgr || {}, // Include the WGR data
              technical_data: obiProduct.technical_data || '' // Include technical_data
            });
          }
          break;
          
        case 'rejected_marked':
          // Set a special flag instead of a dummy product
          setCurrentObiProduct({
            isRejectedMarked: true,
            // No other product data needed
          });
          break;
          
        case 'rejected':
          // Set a special flag instead of a dummy product
          setCurrentObiProduct({
            isRejected: true,
            // No other product data needed
          });
          break;
          
        default:
          console.warn(`Unknown product status: ${transformedProduct.status}`);
      }
    } catch (err) {
      console.error('Search failed:', err);
      
      // Store the EAN that wasn't found
      setMissingEan(eanInput);
      setModalMessage('Produkt nicht gefunden! Dieses Produkt ist uns bekannt, es liegen aber keine Web-Shop Daten vor, sodass wir keine Matching-Vorschläge generieren können.');
      setIsModalOpen(true);
      resetState();
      
      // Play negative beep for product not found
      playNegativeBeep();
    } finally {
      setIsLoading(false);
      
      // Clear the input field and focus it
      setEanInput('');
      setTimeout(() => {
        if (eanInputRef.current) {
          eanInputRef.current.focus();
        }
      }, 0);
    }
  };

  // Handle OBI product search by article number
  const handleObiProductSearch = async () => {
    if (!articleNumberInput.trim()) return;
    
    setIsLoading(true);
    
    try {
      // Get OBI product by article number
      const obiProduct = await productsAPI.getObiProductByArticleNumber(articleNumberInput);
      
      // Parse image_url if it's a JSON string
      let imageArray = [];
      if (obiProduct.image_url) {
        if (typeof obiProduct.image_url === 'string' && obiProduct.image_url.startsWith('[') && obiProduct.image_url.endsWith(']')) {
          try {
            imageArray = JSON.parse(obiProduct.image_url);
          } catch (e) {
            console.error('Error parsing OBI product image URL JSON:', e);
            imageArray = [obiProduct.image_url];
          }
        } else if (Array.isArray(obiProduct.image_url)) {
          imageArray = obiProduct.image_url;
        } else {
          imageArray = [obiProduct.image_url];
        }
      }
      
      // Transform the OBI product
      const transformedObiProduct = {
        ...obiProduct,
        name: obiProduct.name || '',
        dimensions: obiProduct.dimensions || '',
        price: obiProduct.price || 0,
        description: obiProduct.product_description || '',
        detailed_description: obiProduct.detailed_description || '',
        ean: obiProduct.ean || '',
        image: imageArray,
        wgr: obiProduct.wgr || {} ,
        technical_data: obiProduct.technical_data || ''
      };
      
      // Set the current OBI product
      setCurrentObiProduct(transformedObiProduct);
      
    } catch (err) {
      console.error('OBI product search failed:', err);
      setModalMessage('OBI Produkt nicht gefunden');
      setIsModalOpen(true);
    } finally {
      setIsLoading(false);
      setArticleNumberInput('');
    }
  };

  // Handle key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      handleSearch();
    }
  };
  
  // Handle key press in article number search input
  const handleArticleNumberKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      handleObiProductSearch();
    }
  };

  // Handle "Kombinierbar" button click
  const handleKombinierbar = async () => {
    if (!sourceProduct || !currentObiProduct) return;
    
    setIsLoading(true);
    
    try {
      await productsAPI.addKombinierbar(sourceProduct.id, currentObiProduct.id);
      
      // Show success indicator
      setShowSuccessIndicator(true);
      
      // Reset state for next search
      resetState();
      setEanInput('');
      
      // Focus the input field with setTimeout to ensure it happens after React rendering
      setTimeout(() => {
        if (eanInputRef.current) {
          eanInputRef.current.focus();
        }
      }, 0);
    } catch (err) {
      console.error('Failed to mark as combinable:', err);
      setModalMessage('Failed to mark as combinable. Please try again.');
      setIsModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle "Nicht Kombinierbar" button click
  const handleNichtKombinierbar = async () => {
    if (!sourceProduct || !currentObiProduct) return;
    
    setIsLoading(true);
    
    try {
      await productsAPI.addNichtKombinierbar(sourceProduct.id, currentObiProduct.id);
      
      // Show success indicator
      setShowSuccessIndicator(true);
      
      // Reset state for next search
      resetState();
      setEanInput('');
      
      // Focus the input field with setTimeout to ensure it happens after React rendering
      setTimeout(() => {
        if (eanInputRef.current) {
          eanInputRef.current.focus();
        }
      }, 0);
    } catch (err) {
      console.error('Failed to mark as not combinable:', err);
      setModalMessage('Failed to mark as not combinable. Please try again.');
      setIsModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle "Ablehnen" button click
  const handleAblehnen = () => {
    // Move to the next proposed match
    setCurrentMatchIndex(prevIndex => prevIndex + 1);
    
    // Focus the input field with setTimeout to ensure it happens after React rendering
    setTimeout(() => {
      if (eanInputRef.current) {
        eanInputRef.current.focus();
      }
    }, 0);
  };
  
  // Handle "Ablehnen" button click for rejected_marked status
  const handleAblehnenRejectedMarked = () => {
    // Clear the current OBI product to allow searching for another one
    setCurrentObiProduct({
      isRejectedMarked: true,
      // No other product data needed
    });
    
    // Focus the article number input field
    setTimeout(() => {
      if (articleNumberInputRef.current) {
        articleNumberInputRef.current.focus();
      }
    }, 0);
  };
  
  // Handle "Ablehnen (Obi Produkt existiert nicht)" button click for rejected_marked status
  const handleRejectNotExistsInObi = async () => {
    if (!sourceProduct) return;
    
    setIsLoading(true);
    
    try {
      // Call the addReject API with existsInObiMarket set to false
      await productsAPI.addReject(sourceProduct.id, false);
      
      // Show success indicator
      setShowSuccessIndicator(true);
      
      // Reset state for next search
      resetState();
      setEanInput('');
      
      // Focus the input field with setTimeout to ensure it happens after React rendering
      setTimeout(() => {
        if (eanInputRef.current) {
          eanInputRef.current.focus();
        }
      }, 0);
    } catch (err) {
      console.error('Failed to reject product:', err);
      setModalMessage('Failed to reject product. Please try again.');
      setIsModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle confirmation for rejecting all matches
  const handleConfirmReject = async (existsInObiMarket) => {
    setShowRejectConfirmation(false);
    
    if (!sourceProduct) return;
    
    setIsLoading(true);
    
    try {
      const rejectResult = await productsAPI.addReject(sourceProduct.id, existsInObiMarket);
      
      // Show success indicator
      setShowSuccessIndicator(true);
      
      // If existsInObiMarket is true and we got updated product data, use it
      if (existsInObiMarket && rejectResult.updatedProduct) {
        console.log('Using updated product from reject API response');
        
        try {
          // Process the updated product
          const product = rejectResult.updatedProduct;
          console.log('Updated product data:', product);
          
          // Check for WGR exclusion
          if (product.wgr && product.wgr.ausschluss) {
            console.log('Product has WGR exclusion');
            setModalMessage('Ausschluss WGR');
            setIsModalOpen(true);
            resetState();
            return;
          }
          
          // Check for no matches
          if (!product.matches || product.matches.length === 0) {
            console.log('Product has no matches');
            setModalMessage('Keine Matches vorhanden');
            setIsModalOpen(true);
            resetState();
            return;
          }
          
          // Transform the product to maintain compatibility with frontend
          const transformedProduct = {
            ...product,
            status: product.matching_status, // Map matching_status to status for backward compatibility
            image: product.image_url ? (Array.isArray(product.image_url) ? product.image_url : [product.image_url]) : [],
            description: product.description || '', // Ensure description is at least an empty string
            detailed_description: product.detailed_description || '', // Ensure detailed_description is at least an empty string
            dimensions: product.dimensions || '', // Ensure dimensions is at least an empty string
            price: product.price || 0, // Ensure price has a default value
            ean: product.ean || '' // Ensure EAN is available
          };
          
          console.log('Transformed product:', transformedProduct);
          console.log('Product status:', transformedProduct.status);
          
          // Now that we have the new data, reset the state
          resetState();
          
          // Set the source product
          setSourceProduct(transformedProduct);
          
          // For rejected_marked status, set the appropriate OBI product state
          if (transformedProduct.status === 'rejected_marked') {
            setCurrentObiProduct({
              isRejectedMarked: true,
              // No other product data needed
            });
          }
          
          console.log('Product updated to rejected_marked status');
        } catch (loadErr) {
          console.error('Failed to process updated product after reject:', loadErr);
          setModalMessage('Failed to process updated product. Please try again.');
          setIsModalOpen(true);
          resetState();
        }
      } else {
        // Standard behavior for final rejection (existsInObiMarket = false) or no updated product
        resetState();
        setEanInput('');
        
        // Focus the input field with setTimeout to ensure it happens after React rendering
        setTimeout(() => {
          if (eanInputRef.current) {
            eanInputRef.current.focus();
          }
        }, 0);
      }
    } catch (err) {
      console.error('Failed to reject product:', err);
      setModalMessage('Failed to reject product. Please try again.');
      setIsModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle "Rückgängig machen" button click
  const handleRevertMatch = async () => {
    if (!sourceProduct) return;
    
    setIsLoading(true);
    
    // Store the current product's ID for reloading after revert
    const productId = sourceProduct.id;
    
    console.log('Reverting match for product with ID:', productId);
    
    try {
      // Call the revert API
      const revertResult = await productsAPI.revertMatch(productId);
      console.log('Revert API response:', revertResult);
      
      // Show success indicator
      setShowSuccessIndicator(true);
      
      // Check if the API returned the updated product
      if (revertResult.updatedProduct) {
        console.log('Using updated product from API response');
        
        try {
          // Process the updated product
          const product = revertResult.updatedProduct;
          console.log('Updated product data:', product);
          
          // Check for WGR exclusion
          if (product.wgr && product.wgr.ausschluss) {
            console.log('Product has WGR exclusion');
            setModalMessage('Ausschluss WGR');
            setIsModalOpen(true);
            resetState();
            return;
          }
          
          // Check for no matches
          if (!product.matches || product.matches.length === 0) {
            console.log('Product has no matches');
            setModalMessage('Keine Matches vorhanden');
            setIsModalOpen(true);
            resetState();
            return;
          }
          
          // Transform the product to maintain compatibility with frontend
          const transformedProduct = {
            ...product,
            status: product.matching_status, // Map matching_status to status for backward compatibility
            image: product.image_url ? (Array.isArray(product.image_url) ? product.image_url : [product.image_url]) : [],
            description: product.description || '', // Ensure description is at least an empty string
            detailed_description: product.detailed_description || '', // Ensure detailed_description is at least an empty string
            dimensions: product.dimensions || '', // Ensure dimensions is at least an empty string
            price: product.price || 0, // Ensure price has a default value
            ean: product.ean || ''  // Ensure EAN is available
          };
          
          console.log('Transformed product:', transformedProduct);
          console.log('Product status:', transformedProduct.status);
          
          // Now that we have the new data, reset the state
          resetState();
          
          // Set the source product
          setSourceProduct(transformedProduct);
          
          // Handle different product statuses
          switch (transformedProduct.status) {
            case 'proposed':
              console.log('Product has proposed status, processing matches');
              // Extract proposed matches
              const proposed = product.matches.filter(match => match.status === 'proposed');
              console.log('Found proposed matches:', proposed.length);
              
              // Transform matches to include obiProduct property for compatibility
              const transformedMatches = proposed.map(match => {
                // Ensure obi_product has default values for required fields
                const obiProduct = match.obi_product || {};
                
                // Parse image_url if it's a JSON string
                let imageArray = [];
                if (obiProduct.image_url) {
                  if (typeof obiProduct.image_url === 'string' && obiProduct.image_url.startsWith('[') && obiProduct.image_url.endsWith(']')) {
                    try {
                      imageArray = JSON.parse(obiProduct.image_url);
                    } catch (e) {
                      console.error('Error parsing OBI product image URL JSON:', e);
                      imageArray = [obiProduct.image_url];
                    }
                  } else if (Array.isArray(obiProduct.image_url)) {
                    imageArray = obiProduct.image_url;
                  } else {
                    imageArray = [obiProduct.image_url];
                  }
                }
                
                return {
                  ...match,
                  obiProduct: {
                    ...obiProduct,
                    name: obiProduct.name || '',
                    dimensions: obiProduct.dimensions || '',
                    price: obiProduct.price || 0,
                    description: obiProduct.description || '',
                    ean: obiProduct.ean || '',
                    image: imageArray,
                    wgr: obiProduct.wgr || {}, // Include the WGR data
                    technical_data: obiProduct.technical_data || '' // Include technical_data
                  }
                };
              });
              
              console.log('Setting proposed matches:', transformedMatches);
              
              // Sort matches by position in ascending order
              const sortedMatches = transformedMatches.sort((a, b) => {
                // Handle undefined positions by treating them as highest value
                const posA = a.position !== undefined ? a.position : Infinity;
                const posB = b.position !== undefined ? b.position : Infinity;
                return posA - posB;
              });
              
              setProposedMatches(sortedMatches);
              setCurrentMatchIndex(0);
              break;
              
            case 'match_ean':
              console.log('Product has match_ean status');
              // Find the matched product with EAN status
              const eanMatch = product.matches.find(match => match.status === 'match_ean');
              if (eanMatch) {
                console.log('Found EAN match:', eanMatch);
                // Ensure obi_product has default values for required fields
                const obiProduct = eanMatch.obi_product || {};
                
                // Parse image_url if it's a JSON string
                let imageArray = [];
                if (obiProduct.image_url) {
                  if (typeof obiProduct.image_url === 'string' && obiProduct.image_url.startsWith('[') && obiProduct.image_url.endsWith(']')) {
                    try {
                      imageArray = JSON.parse(obiProduct.image_url);
                    } catch (e) {
                      console.error('Error parsing OBI product image URL JSON:', e);
                      imageArray = [obiProduct.image_url];
                    }
                  } else if (Array.isArray(obiProduct.image_url)) {
                    imageArray = obiProduct.image_url;
                  } else {
                    imageArray = [obiProduct.image_url];
                  }
                }
                
                const transformedObiProduct = {
                  ...obiProduct,
                  name: obiProduct.name || '',
                  dimensions: obiProduct.dimensions || '',
                  price: obiProduct.price || 0,
                  description: obiProduct.description || '',
                  ean: obiProduct.ean || '',
                  image: imageArray,
                  wgr: obiProduct.wgr || {}, // Include the WGR data
                  technical_data: obiProduct.technical_data || '' // Include technical_data
                };
                
                console.log('Setting OBI product for EAN match:', transformedObiProduct);
                setCurrentObiProduct(transformedObiProduct);
              }
              break;
              
            case 'match_kombinierbar':
            case 'match_nicht_kombinierbar':
              console.log('Product has match_kombinierbar or match_nicht_kombinierbar status');
              // Find the matched product
              const matchedProduct = product.matches.find(
                match => match.status === 'match_kombinierbar' || match.status === 'match_nicht_kombinierbar'
              );
              if (matchedProduct) {
                console.log('Found matched product:', matchedProduct);
                // Ensure obi_product has default values for required fields
                const obiProduct = matchedProduct.obi_product || {};
                
                // Parse image_url if it's a JSON string
                let imageArray = [];
                if (obiProduct.image_url) {
                  if (typeof obiProduct.image_url === 'string' && obiProduct.image_url.startsWith('[') && obiProduct.image_url.endsWith(']')) {
                    try {
                      imageArray = JSON.parse(obiProduct.image_url);
                    } catch (e) {
                      console.error('Error parsing OBI product image URL JSON:', e);
                      imageArray = [obiProduct.image_url];
                    }
                  } else if (Array.isArray(obiProduct.image_url)) {
                    imageArray = obiProduct.image_url;
                  } else {
                    imageArray = [obiProduct.image_url];
                  }
                }
                
                const transformedObiProduct = {
                  ...obiProduct,
                  name: obiProduct.name || '',
                  dimensions: obiProduct.dimensions || '',
                  price: obiProduct.price || 0,
                  description: obiProduct.description || '',
                  ean: obiProduct.ean || '',
                  image: imageArray,
                  wgr: obiProduct.wgr || {}, // Include the WGR data
                  technical_data: obiProduct.technical_data || '' // Include technical_data
                };
                
                console.log('Setting OBI product for matched product:', transformedObiProduct);
                setCurrentObiProduct(transformedObiProduct);
              }
              break;
              
            case 'rejected':
              console.log('Product has rejected status');
              // Set a special flag instead of a dummy product
              setCurrentObiProduct({
                isRejected: true,
                // No other product data needed
              });
              
              console.log('Setting rejected flag for OBI product');
              break;
              
            case 'rejected_marked':
              console.log('Product has rejected_marked status');
              // Set a special flag instead of a dummy product
              setCurrentObiProduct({
                isRejectedMarked: true,
                // No other product data needed
              });
              
              console.log('Setting rejected_marked flag for OBI product');
              break;
              
            default:
              console.warn(`Unknown product status: ${transformedProduct.status}`);
          }
        } catch (loadErr) {
          console.error('Failed to reload product after revert:', loadErr);
          setModalMessage('Failed to reload product after revert. Please try again.');
          setIsModalOpen(true);
          resetState();
        }
      } else {
        console.log('No EAN available, cannot reload product data');
        setModalMessage('Produkt konnte nicht automatisch neu geladen werden. Bitte suchen Sie das Produkt erneut.');
        setIsModalOpen(true);
        resetState();
        setEanInput('');
        setTimeout(() => {
          if (eanInputRef.current) {
            eanInputRef.current.focus();
          }
        }, 0);
      }
    } catch (err) {
      console.error('Failed to revert match:', err);
      setModalMessage('Failed to revert match. Please try again.');
      setIsModalOpen(true);
      
      // Reset state and clear the input field
      resetState();
      setEanInput('');
      setTimeout(() => {
        if (eanInputRef.current) {
          eanInputRef.current.focus();
        }
      }, 0);
    } finally {
      // Add a small delay before setting isLoading to false
      // to ensure state updates have time to process
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  // Determine which buttons to show based on product status
  const renderActionButtons = () => {
    if (!sourceProduct) return null;
    
    switch (sourceProduct.status) {
      case 'proposed':
        if (!currentObiProduct) return null;
        return (
          <div className="action-buttons">
            <button 
              className="action-button combinable"
              onClick={handleKombinierbar}
              disabled={isLoading}
            >
              Match kombinierbar
            </button>
            <button 
              className="action-button not-combinable"
              onClick={handleNichtKombinierbar}
              disabled={isLoading}
            >
              Alt vor Neu
            </button>
            <button 
              className="action-button reject"
              onClick={handleAblehnen}
              disabled={isLoading}
            >
              Ablehnen
            </button>
          </div>
        );
        
      case 'rejected_marked':
        // Show different buttons based on whether an OBI product has been selected
        if (currentObiProduct && !currentObiProduct.isRejectedMarked) {
          // If a real OBI product has been selected, show all buttons
          return (
            <div className="action-buttons">
              <button 
                className="action-button combinable"
                onClick={handleKombinierbar}
                disabled={isLoading}
              >
                Match kombinierbar
              </button>
              <button 
                className="action-button not-combinable"
                onClick={handleNichtKombinierbar}
                disabled={isLoading}
              >
                Match nicht kombinierbar
              </button>
              <button 
                className="action-button reject"
                onClick={handleAblehnenRejectedMarked}
                disabled={isLoading}
              >
                Ablehnen
              </button>
            </div>
          );
        } else {
          // If no OBI product has been selected, show both the revert button and the reject button
          return (
            <div className="action-buttons">
              <button 
                className="action-button revert"
                onClick={handleRevertMatch}
                disabled={isLoading}
              >
                Rückgängig machen
              </button>
              <button 
                className="action-button reject"
                onClick={handleRejectNotExistsInObi}
                disabled={isLoading}
              >
                Ablehnen <br></br>(Obi Produkt existiert nicht)
              </button>
            </div>
          );
        }
        
      case 'match_ean':
        // For EAN matches, don't show any buttons
        return null;
        
      case 'match_kombinierbar':
      case 'match_nicht_kombinierbar':
      case 'rejected':
        return (
          <div className="action-buttons">
            <button 
              className="action-button revert"
              onClick={handleRevertMatch}
              disabled={isLoading}
            >
              Rückgängig machen
            </button>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="product-matching-page">
      {/* Success Indicator */}
      <SuccessIndicator 
        show={showSuccessIndicator} 
        onAnimationComplete={() => setShowSuccessIndicator(false)} 
      />
      {/* Modal for errors and notifications */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        onOkClick={() => {
          // For EAN not found case
          if (missingEan) {
            productsAPI.reportMissingEAN(missingEan)
              .then(() => console.log(`Reported missing EAN: ${missingEan}`))
              .catch(err => console.error('Failed to report missing EAN:', err));
            
            setMissingEan('');
          }
          
          // Clear the input field
          setEanInput('');
          
          // Focus the input field with setTimeout to ensure it happens after React rendering
          setTimeout(() => {
            if (eanInputRef.current) {
              eanInputRef.current.focus();
            }
          }, 0);
          
          // Close the modal
          setIsModalOpen(false);
        }}
        title={missingEan ? "Information" : "Information"}
        // Add auto-close for specific error messages
        autoCloseTime={
          modalMessage === 'Produkt nicht gefunden! Dieses Produkt ist uns bekannt, es liegen aber keine Web-Shop Daten vor, sodass wir keine Matching-Vorschläge generieren können.' || 
          modalMessage === 'Ausschluss WGR' ? 
          1000 : undefined
        }
      >
        <p>{modalMessage}</p>
      </Modal>
      
      {/* Confirmation Modal for rejecting all matches */}
      <Modal 
        isOpen={showRejectConfirmation} 
        onClose={() => setShowRejectConfirmation(false)}
        title="Bestätigung"
        hideOkButton={true}
      >
        <p>Trotz Ablehnung der Vorschläge: <br></br>Gibt es sicher ein matchendes Produkt im Obi-Marktsortiment? <br></br> 
        </p>
        <div className="modal-footer">
          <button 
            className="modal-button" 
            style = {{height: "50px", width: "50%"}} 
            onClick={() => handleConfirmReject(true)}
          >
            Ja <br></br> <p style={{fontSize:"12px"}}>Zur Nachbearbeitung merken</p>
          </button> 
          <button 
            className="modal-button"
            style = {{height: "50px", width: "50%"}} 
            onClick={() => handleConfirmReject(false)}
          >
            Nein
          </button>
        </div>
      </Modal>
      
      {/* Search Bar */}
      <div className="search-container">
        <div className="search-bar">
          <label htmlFor="ean-input">EAN:</label>
          <input
            id="ean-input"
            type="text"
            value={eanInput}
            onChange={(e) => setEanInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="EAN eingeben"
            disabled={isLoading}
            ref={eanInputRef}
            autoFocus
          />
          <button 
            className="search-button"
            onClick={handleSearch}
            disabled={isLoading || !eanInput.trim()}
          >
            Suchen
          </button>
        </div>
      </div>

      {/* Product Comparison Area */}
      <div className="product-comparison">
        {/* Source Product Panel (Hagebau) */}
        <div className="product-panel source-panel">
          {sourceProduct ? (
            <>
              <div className="banners-container">
                {/* Empty container to match height with OBI panel */}
              </div>
              
              <div className="product-image-container">
                <ImageCarousel 
                  images={sourceProduct.image} 
                  alt={sourceProduct.name} 
                  className="product-image"
                />
                <button className="fp-button">FP</button>
                <a href={"https://www.barcodelookup.com/" + sourceProduct.EAN} target="_blank" className="link-button1">Link1</a>
                <a href={"https://www.ean-search.org/?q=" + sourceProduct.EAN} target="_blank" className="link-button2">Link2</a>
              </div>
              
              <h3 className="product-name">{sourceProduct.name}</h3>
              
              <div className="product-details">
                <div className="detail-row">
                  <span className="detail-label">Produkt-Maße:</span>
                  <span className="detail-value">
                    {(() => {
                      console.log(sourceProduct.technical_data)
                      const formattedMeasurements = extractMeasurements(sourceProduct.technical_data);
                      return formattedMeasurements || sourceProduct.dimensions || 'N/A';
                    })()}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Preis:</span>
                  <span className="detail-value">{sourceProduct.price}€</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Beschreibung:</span>
                  <div style={{height:"175px", overflow: "auto"}}>
                    <span className="detail-value">{stripHtmlTags(sourceProduct.detailed_description)}</span>
                    <div className="detail-value technical-details">
                        {renderJsonAsLists(sourceProduct.technical_data)}
                      </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-product">
              <p></p>
            </div>
          )}
        </div>

        {/* Target Product Panel (OBI) */}
        <div className="product-panel target-panel">
          {currentObiProduct ? (
            <>
              {/* Search Bar for OBI products when status is rejected_marked */}
              {sourceProduct && sourceProduct.status === 'rejected_marked' && 
                currentObiProduct.isRejectedMarked && (
                <div className="search-container article-search-container">
                  <p className="search-instruction">
                    Bitte die Artikelnummer des matchenden Obi Produktes eingeben
                  </p>
                  <div className="search-bar">
                    <label htmlFor="article-number-input">OBI Artikelnummer:</label>
                    <input
                      id="article-number-input"
                      type="text"
                      value={articleNumberInput}
                      onChange={(e) => setArticleNumberInput(e.target.value)}
                      onKeyDown={handleArticleNumberKeyPress}
                      placeholder="Artikelnummer eingeben"
                      disabled={isLoading}
                      ref={articleNumberInputRef}
                    />
                    <button 
                      className="search-button"
                      onClick={handleObiProductSearch}
                      disabled={isLoading || !articleNumberInput.trim()}
                    >
                      Suchen
                    </button>
                  </div>
                </div>
              )}
              
              {/* Add spacer for rejected status */}
              {sourceProduct && sourceProduct.status === 'rejected' && 
                currentObiProduct.isRejected && (
                <div className="rejected-spacer">
                  <p className="search-instruction">
                    Dieses Produkt wurde abgelehnt. Alle vorgeschlagenen Matches wurden zurückgewiesen.
                  </p>
                </div>
              )}
              
              <div className="banners-container">
                {currentObiProduct.wgr && currentObiProduct.wgr.vp_mass_kontrolle && (
                  <div className="warning-banner">
                    ACHTUNG: Platzierbarkeit mit Zielraster prüfen
                  </div>
                )}
                
                {sourceProduct && sourceProduct.status === 'match_ean' && (
                  <div className="status-banner status-ean">
                    EAN Match
                  </div>
                )}
                
                {sourceProduct && sourceProduct.status === 'match_kombinierbar' && (
                  <div className="status-banner status-kombinierbar">
                    Kombinierbares Match
                  </div>
                )}
                
                {sourceProduct && sourceProduct.status === 'match_nicht_kombinierbar' && (
                  <div className="status-banner status-nicht-kombinierbar">
                    Nicht kombinierbares Match
                  </div>
                )}
                
                {sourceProduct && sourceProduct.status === 'rejected_marked' && (
                  <div className="status-banner status-rejected">
                    Alle Matches abgelehnt (markiert)
                  </div>
                )}
                
                {sourceProduct && sourceProduct.status === 'rejected' && (
                  <div className="status-banner status-rejected">
                    Alle Matches Abgelehnt
                  </div>
                )}
              </div>
              
              {/* Only show product details if it's not a rejected status or if it's a real product */}
              {!(currentObiProduct.isRejected || currentObiProduct.isRejectedMarked) && (
                <>
                  <div className="product-image-container">
                    <ImageCarousel 
                      images={currentObiProduct.image} 
                      alt={currentObiProduct.name} 
                      className="product-image"
                    />
                    <div className="target-header">
                      <img src="/img/OBI_logo.png" alt="OBI Logo" className="obi-logo" />
                    </div>
                  </div>
                  
                  <h3 className="product-name">{currentObiProduct.name}</h3>
                  
                  <div className="product-details">
                    <div className="detail-row">
                      <span className="detail-label">VP-Maße:</span>
                      <span className="detail-value">
                        {(() => {
                          // Try to extract dimensions from technical_data
                          const techDimensions = extractDimensions(currentObiProduct.technical_data);
                          
                          if (techDimensions) {
                            return `${techDimensions.height} × ${techDimensions.width} × ${techDimensions.depth} (HxBxT)`;
                          }
                          
                          // Fall back to the original dimensions field
                          return currentObiProduct.dimensions || '';
                        })()}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Preis:</span>
                      <span 
                        className={`detail-value ${
                          sourceProduct && isPriceDifferenceSignificant(sourceProduct.price, currentObiProduct.price) 
                            ? 'price-difference' 
                            : ''
                        }`}
                      >
                        {currentObiProduct.price}€
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Platzierung:</span>
                      <span className="detail-value">{currentObiProduct.platzierung}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Beschreibung:</span>
                      <div style={{height:"75px", overflow: "auto"}}>
                        <span className="detail-value">
                          {stripHtmlTags(currentObiProduct.description + " " + currentObiProduct.detailed_description)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Technical Details 
                    <div className="detail-row">
                      <span className="detail-label">Technische Details:</span>
                      <div style={{height:"150px", overflow: "auto"}}>
                        <div className="detail-value technical-details">
                          {renderJsonAsLists(currentObiProduct.technical_data)}
                        </div>
                      </div>
                    </div>*/}
                    {/**
                     * <div className="detail-row">
                        <span className="detail-label">EAN:</span>
                        <span className="detail-value">{currentObiProduct.ean}</span>
                      </div>
                     * 
                     */}
                    </div>
                </>
              )}
                
               
              {renderActionButtons()}
            </>
          ) : (
            <div className="empty-product">
              <p></p>
            </div>
          )}
        </div>
      </div>
      
      {/* Audio elements for beep sounds */}
      <audio ref={positiveBeepRef} src="/sounds/positive-beep.wav" preload="auto"></audio>
      <audio ref={negativeBeepRef} src="/sounds/negative-beep.mp3" preload="auto"></audio>
    </div>
  );
};

export default ProductMatchingPage;
